import { baseApi } from "../../../core/data/rtk/baseQuery";
import { ApiResponseType } from "../../../core/data/rtk/apiResponseType";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OfflinePayment {
  id: number;
  student_id: number;
  center_id: number;
  amount: string | number;
  payment_date: string | null;
  payment_method: string;
  reference_number: string;
  remarks: string | null;
  receipt_path: string;
  status: "pending" | "approved" | "rejected";
  created_by: number;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface OfflinePaymentListData {
  items: OfflinePayment[];
  pageSize: number;
  currentPage: number;
  totalItems: number;
  totalPages: number;
}

export interface GetAllOfflinePaymentsParams {
  size?: number;
  page?: number;
  search?: string;
  student_id?: number;
  center_id?: number;
  status?: string;
  from_date?: string;
  to_date?: string;
}

export interface CreateOfflinePaymentRequest {
  student_id: number;
  center_id: number;
  amount: number;
  payment_date: string;
  payment_method?: string;
  reference_number?: string;
  receipt: File;
}

export interface UpdateOfflinePaymentRequest {
  id: number | string;
  formData: FormData;
}

// ─── API Slice ────────────────────────────────────────────────────────────────

export const offlinePaymentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET all offline payments
    getAllOfflinePayments: builder.query<
      ApiResponseType<OfflinePaymentListData>,
      GetAllOfflinePaymentsParams
    >({
      query: (params) => {
        const qp = new URLSearchParams();
        if (params?.size) qp.append("size", params.size.toString());
        if (params?.page) qp.append("page", params.page.toString());
        if (params?.search) qp.append("search", params.search);
        if (params?.student_id) qp.append("student_id", params.student_id.toString());
        if (params?.center_id) qp.append("center_id", params.center_id.toString());
        if (params?.status) qp.append("status", params.status);
        if (params?.from_date) qp.append("from_date", params.from_date);
        if (params?.to_date) qp.append("to_date", params.to_date);
        return `student-offline-payments/get-all?${qp.toString()}`;
      },
      providesTags: ["OfflinePayments"],
    }),

    // POST create offline payment (multipart/form-data)
    createOfflinePayment: builder.mutation<
      ApiResponseType<{ payment: OfflinePayment }>,
      FormData
    >({
      query: (formData) => ({
        url: "student-offline-payments",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["OfflinePayments", "Invoices", "Students"],
    }),

    // POST update offline payment (multipart/form-data)
    updateOfflinePayment: builder.mutation<
      ApiResponseType<{ payment: OfflinePayment }>,
      UpdateOfflinePaymentRequest
    >({
      query: ({ id, formData }) => ({
        url: `student-offline-payments/update/${id}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["OfflinePayments", "Invoices", "Students"],
    }),
    // GET offline payment by ID
    getOfflinePaymentById: builder.query<
      ApiResponseType<{ payment: OfflinePayment }>,
      number | string
    >({
      query: (id) => `student-offline-payments/${id}`,
      providesTags: (result, error, id) => [{ type: "OfflinePayments", id }],
    }),
  }),
});

export const {
  useGetAllOfflinePaymentsQuery,
  useGetOfflinePaymentByIdQuery,
  useCreateOfflinePaymentMutation,
  useUpdateOfflinePaymentMutation,
} = offlinePaymentApi;
